import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  useTheme as useMuiTheme,
  alpha,
  Chip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Pix as PixIcon,
  QrCode as QrCodeIcon,
  Payment as BarcodeIcon, // Usando Payment como fallback para Barcode
  CheckCircle as PaidIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { apiRequest } from '../services/api';
import { toast } from 'react-toastify';
import { format, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Helmet } from 'react-helmet-async';
import { env } from '../utils/env';

interface InvoiceData {
  id: number;
  valor: number;
  acrescimo: number;
  desconto: number;
  valor_total: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'aberto' | 'pago' | 'vencido' | 'parcialmente_pago' | 'cancelado';
  descricao: string;
  linha_digitavel?: string;
  codigo_barras?: string;
  qr_code_pix?: string;
  codigo_pix?: string;
  url_boleto?: string;
  url_pagamento?: string;
  cliente: {
    nome: string;
    cpf_cnpj: string;
    endereco: string;
    numero: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
  escritorio?: {
    razao_social: string;
    nome_fantasia: string;
    cnpj: string;
    logo?: string;
    telefone_celular?: string;
    email_institucional?: string;
  };
  gateway_tipo?: string;
  company_logo?: string;
}

const PublicInvoicePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const muiTheme = useMuiTheme();

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const response = await apiRequest.get(`/public/invoice/${token}`);
        if (response.success) {
          setInvoice(response.data);
        } else {
          setError(response.message || 'Fatura não encontrada');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar fatura');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvoice();
    }
  }, [token]);

  const handleCopy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado com sucesso!`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago': return muiTheme.palette.success.main;
      case 'vencido': return muiTheme.palette.error.main;
      case 'cancelado': return muiTheme.palette.grey[500];
      default: return muiTheme.palette.primary.main;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pago': return 'PAGA';
      case 'vencido': return 'VENCIDA';
      case 'cancelado': return 'CANCELADA';
      case 'parcialmente_pago': return 'PAGO PARCIAL';
      default: return 'EM ABERTO';
    }
  };

  const handleDownloadPdf = () => {
    if (!token) return;
    const url = `${env.API_URL}/api/public/invoice/${token}/pdf`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc" p={3}>
        <ErrorIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {error || 'Link inválido ou expirado'}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  const isPaid = invoice.status === 'pago';
  const vencimentoDate = new Date(invoice.data_vencimento + 'T12:00:00'); // Evita problemas de fuso horário
  const isExpired = invoice.status === 'vencido' || isBefore(vencimentoDate, new Date());

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f0f2f5',
      py: { xs: 2, md: 5 },
      px: { xs: 1, md: 2 },
      backgroundImage: 'radial-gradient(circle at 50% 0%, #ffffff 0%, #f0f2f5 100%)'
    }}>
      <Helmet>
        <title>Fatura - {invoice.escritorio?.nome_fantasia || 'SysUp'}</title>
      </Helmet>

      <Container maxWidth="md">
        {/* Header/Logo */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          {invoice.company_logo ? (
            <img src={invoice.company_logo} alt="Logo" style={{ maxHeight: 80, maxWidth: '100%', marginBottom: 16, borderRadius: 8 }} />
          ) : invoice.escritorio?.logo ? (
            <img src={invoice.escritorio.logo} alt="Logo" style={{ maxHeight: 80, maxWidth: '100%', marginBottom: 16, borderRadius: 8 }} />
          ) : (
            <Typography variant="h4" fontWeight="800" color="primary" sx={{ letterSpacing: -1 }}>
              SysUp<span style={{ color: muiTheme.palette.text.secondary }}>Invoice</span>
            </Typography>
          )}
        </Box>

        <Grid container spacing={1}>
          {/* Main Card */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{
              p: 2,
              borderRadius: 4,
              border: '1px solid',
              borderColor: alpha(muiTheme.palette.divider, 0.1),
              boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Status Ribbon/Badge */}
              <Box sx={{
                position: 'absolute',
                top: 20,
                right: 20
              }}>
                <Chip
                  label={getStatusLabel(invoice.status)}
                  sx={{
                    bgcolor: alpha(getStatusColor(invoice.status), 0.1),
                    color: getStatusColor(invoice.status),
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                  }}
                />
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                DETALHES DA FATURA
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h3" fontWeight="800" sx={{ color: '#1e293b', fontSize: { xs: '2rem', md: '3rem' } }}>
                  R$ {Number(invoice.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Vencimento:
                  </Typography>
                  <Typography variant="body1" fontWeight="700" color={isExpired && !isPaid ? 'error.main' : 'text.primary'}>
                    {format(vencimentoDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </Typography>
                </Stack>
              </Box>

              <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    Pagador
                  </Typography>
                  <Typography variant="body1" fontWeight="600">{invoice.cliente.nome}</Typography>
                  <Typography variant="body2" color="text.secondary">{invoice.cliente.cpf_cnpj}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {invoice.cliente.endereco}, {invoice.cliente.numero} - {invoice.cliente.bairro}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {invoice.cliente.cidade} - {invoice.cliente.estado}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    Descrição
                  </Typography>
                  <Typography variant="body1">{invoice.descricao}</Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                  onClick={handleDownloadPdf}
                >
                  Download PDF
                </Button>
              </Box>
            </Paper>
            {/* Boleto Payment */}
            {invoice.linha_digitavel && (
              <Paper elevation={0} sx={{
                mt: 1,
                p: 1,
                borderRadius: 4,
                border: '1px solid',
                borderColor: alpha(muiTheme.palette.divider, 0.1),
                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
              }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BarcodeIcon color="action" />
                  <Typography variant="h6" fontWeight="800">Boleto Bancário</Typography>
                </Stack>

                <Box sx={{ bgcolor: alpha(muiTheme.palette.action.hover, 0.05), p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">LINHA DIGITÁVEL</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', mt: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {invoice.linha_digitavel}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => handleCopy(invoice.linha_digitavel || '', 'Linha digitável')}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Copiar Linha
                </Button>

                {invoice.codigo_barras && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                    <Barcode
                      value={invoice.codigo_barras}
                      width={1.6}
                      height={50}
                      displayValue={false}
                      background="transparent"
                    />
                  </Box>
                )}
              </Paper>
            )}
          </Grid>

          {/* Payment Card */}
          <Grid item xs={12} md={5}>
            {!isPaid ? (
              <Stack spacing={2}>
                {/* PIX Payment */}
                {(invoice.qr_code_pix || invoice.codigo_pix) && (
                  <Paper elevation={0} sx={{
                    p: 2,
                    borderRadius: 4,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: alpha(muiTheme.palette.primary.main, 0.2),
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)'
                  }}>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                      <PixIcon sx={{ color: '#32bcad' }} />
                      <Typography variant="h6" fontWeight="800" color="#32bcad">Pagar com PIX</Typography>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Aponte a câmera do seu celular para o QR Code abaixo
                    </Typography>

                    <Box sx={{
                      p: 0,
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      display: 'inline-block',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      mb: 2
                    }}>
                      <QRCodeSVG
                        value={invoice.qr_code_pix || invoice.codigo_pix || ''}
                        size={180}
                        level="H"
                        includeMargin={true}
                      />
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<CopyIcon />}
                      onClick={() => handleCopy(invoice.qr_code_pix || invoice.codigo_pix || '', 'Código PIX')}
                      sx={{
                        bgcolor: '#32bcad',
                        '&:hover': { bgcolor: '#2aa598' },
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold'
                      }}
                    >
                      Copiar Código PIX
                    </Button>
                  </Paper>
                )}
              </Stack>
            ) : (
              /* Success/Paid View */
              <Paper elevation={0} sx={{
                p: 4,
                borderRadius: 4,
                textAlign: 'center',
                border: '1px solid',
                borderColor: alpha(muiTheme.palette.success.main, 0.2),
                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)',
                bgcolor: alpha(muiTheme.palette.success.main, 0.02)
              }}>
                <PaidIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" fontWeight="800" color="success.main" gutterBottom>
                  Fatura Paga!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esta fatura foi quitada{invoice.data_pagamento ? ` em ${format(new Date(invoice.data_pagamento), 'dd/MM/yyyy HH:mm')}` : ''}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  sx={{ mt: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                  onClick={handleDownloadPdf}
                >
                  Baixar Comprovante
                </Button>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Support info */}
        <Box sx={{ mt: 6, textAlign: 'center', color: 'text.secondary', pb: 4 }}>
          <Typography variant="body2">
            Dúvidas? Entre em contato com <strong>{invoice.escritorio?.nome_fantasia || 'nosso suporte'}</strong>
          </Typography>
          {invoice.escritorio?.telefone_celular && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              WhatsApp: {invoice.escritorio.telefone_celular}
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.5 }}>
            Processado com segurança por SysUp
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicInvoicePage;
